// Money → Overview (spec 044 D100–D112, plan slice 9): the month menu, the four cards, the
// chart's bars and its line, Sessions in and out, Upcoming payouts and the export — from what
// src/services/financial.js reads. Pure: no React, no Firestore, so Jest covers it.
import { toDate } from "./ticketPresentation";
import { displayName, egp, money } from "./queuePresentation";
import { csvText } from "./transactionPresentation";
import { aggregateRows, cardTotals } from "./transactionLedger";
import { sessionsLabel } from "./studentDetail";
import { hoursText } from "./tutorPresentation";
import { owedNow, round2 } from "./cyclePay";
import { lastDayOf } from "./payCycle";
import { changePct, chipOf } from "./reportNumbers";
import { MONTH_NAME, MONTH_SHORT, cairoDateKey, cairoParts, cairoTime, rangeLabel, reportWindows } from "./reportPeriods";

const pad = (n) => String(n).padStart(2, "0");
const keyOf = (y, m) => `${y}-${pad(m)}`;
const partsOf = (key) => String(key).split("-").map(Number);
// Month arithmetic on keys, no clock involved: Date.UTC rolls month 0 and month 13 over.
const shift = (key, by) => {
  const [y, m] = partsOf(key);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return keyOf(d.getUTCFullYear(), d.getUTCMonth() + 1);
};

// "2026-09" → "September 2026".
export const monthLabel = (key) => {
  const [y, m] = partsOf(key);
  return `${MONTH_NAME[m - 1]} ${y}`;
};

// The Cairo month at `now` (D70), as a key: "2026-09".
export const thisMonthKey = (now = new Date()) => {
  const p = cairoParts(now);
  return keyOf(p.y, p.m);
};

// D102: this month and the 11 before it, newest first.
export const monthOptions = (now = new Date()) =>
  Array.from({ length: 12 }, (_, i) => {
    const key = shift(thisMonthKey(now), -i);
    return { key, label: monthLabel(key) };
  });

// D102, D104: the windows a month counts, in reportWindows' shape ({ from, to }, `to`
// exclusive, each with `prev`). This month counts up to now against the same stretch of the
// month before (D78); a past month counts whole against the whole month before.
export const monthWindows = (key, now = new Date()) => {
  if (key === thisMonthKey(now)) return { ...reportWindows("month", now), key, current: true };
  const [y, m] = partsOf(key);
  const from = cairoTime(y, m, 1);
  const to = cairoTime(y, m + 1, 1);
  const prev = { from: cairoTime(y, m - 1, 1), to: from };
  return { key, now, current: false, whole: { from, to, prev }, soFar: { from, to, prev } };
};

const within = (value, { from, to }) => {
  const d = toDate(value);
  return !!d && d >= from && d < to;
};
const minus = (a, b) => (a == null ? null : round2(a - b));
const sumOf = (rows, field = "amount") => rows.reduce((s, r) => s + (Number(r[field]) || 0), 0);

// D103: getLedgerAggregates' answer less the same sums over the test accounts' own rows.
const lessTest = (a, rows) => {
  const t = aggregateRows(rows);
  return {
    paid: { gbp: minus(a.paid?.gbp, t.paid.gbp), sessions: minus(a.paid?.sessions, t.paid.sessions) },
    debit: { sessions: minus(a.debit?.sessions, t.debit.sessions) },
    refund: { sessions: minus(a.refund?.sessions, t.refund.sessions) },
    referral: { sessions: minus(a.referral?.sessions, t.referral.sessions) },
    adjustments:
      a.adjustments == null
        ? null
        : { took: minus(a.adjustments.took, t.adjustments.took), gave: minus(a.adjustments.gave, t.adjustments.gave) },
  };
};

// D104: a payment staff recorded. Undone ones paid nothing (053 D6), and 051's opening-balance
// rows (`kind: "openingBalance"`, 27 Sep 2026) record pay from before the dashboard, not a
// payment made that month. A row with `owedMoved: false` (053 D22) is a receipt for pay the
// opening balance already counts — proof, not more money — so it is left out the same way.
const isPayment = (p) => !p.undoneAt && p.kind !== "openingBalance" && p.owedMoved !== false;

// D103, D104, D106: the page's numbers with the test accounts taken out. `raw` is
// getFinancialMonth's answer plus `tutors` from getPayoutsDue; a read that failed is null, and
// so is every number built on it ("—" on the page). Without the test accounts' own rows the
// ledger numbers cannot be corrected, so they are all null then (Reports' rule).
export const overviewNumbers = (raw, windows) => {
  const { soFar } = windows;
  const tutors = raw.tutors || null;
  const owed = tutors ? owedNow(tutors) : null;
  const internal = new Set((tutors || []).filter((t) => t.isInternal).map((t) => t.id));
  const paidRows = tutors && raw.payouts ? raw.payouts.filter((p) => isPayment(p) && !internal.has(p.teacherId)) : null;
  const paid = paidRows ? { total: round2(sumOf(paidRows)), count: paidRows.length } : null;
  const test = raw.test;
  if (!test || !raw.ledger) {
    return { gross: { cur: null, prev: null }, sold: null, moves: null, owed, paid, failed: true };
  }
  const net = lessTest(raw.ledger, test.rows.filter((r) => within(r.createdAt, soFar)));
  const totals = cardTotals(net);
  const testBefore = sumOf(test.rows.filter((r) => r.status === "succeeded" && within(r.createdAt, soFar.prev)));
  const gross = { cur: totals.collected, prev: minus(raw.collectedBefore, testBefore) };
  const { gave = null, took = null } = net.adjustments || {};
  const moves = {
    sold: totals.sold,
    used: totals.used,
    returned: totals.refunded,
    given: gave == null || took == null ? null : round2(gave - took),
    gave,
    took,
    referral: net.referral.sessions,
  };
  const all = [gross.cur, gross.prev, ...Object.values(moves), owed, paid];
  return { gross, sold: totals.sold, moves, owed, paid, failed: all.some((x) => x == null) };
};

// "25 Oct": the running pay cycle's last day (its `end` is exclusive, payCycle.js).
export const cycleEndLabel = (cycle) => {
  const d = lastDayOf(cycle);
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
};

const perSession = (n) => (n.gross.cur != null && n.sold ? round2(n.gross.cur / n.sold) : null);
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// D104: the four cards, in Figma's order. `pill` is { tone, text, before? } or null — tone
// "up" | "down" | "flat" for the change (chipOf's), else "success" | "warning" | "info".
export const overviewCards = (n, windows, cycle) => {
  const each = perSession(n);
  return [
    {
      key: "gross",
      label: "Gross revenue",
      value: n.gross.cur == null ? "—" : `£${money(n.gross.cur)}`,
      pill: chipOf(n.gross.cur, n.gross.prev, windows.soFar.prev),
    },
    {
      key: "sold",
      label: "Sessions sold",
      value: n.sold == null ? "—" : money(n.sold),
      pill: each == null ? null : { tone: "success", text: `£${each.toFixed(2)} a session` },
    },
    {
      key: "owed",
      label: "Tutor pay owed",
      value: n.owed == null ? "—" : egp(n.owed.total),
      pill: cycle ? { tone: "warning", text: `Cycle ends ${cycleEndLabel(cycle)}` } : null,
    },
    {
      key: "paid",
      label: "Paid to tutors",
      value: n.paid == null ? "—" : egp(n.paid.total),
      pill: n.paid == null ? null : { tone: "info", text: plural(n.paid.count, "payment", "payments") },
    },
  ];
};

// D105: what the chart reads — the day summaries from the first day of the 12 months to the
// chosen month's last day (today while it runs) — in the shape getReportStats and statsSpan
// take ({ whole: { from }, now }).
export const revenueSpan = (key, now = new Date()) => {
  const [y, m] = partsOf(shift(key, -11));
  const [ky, km] = partsOf(key);
  const last = key === thisMonthKey(now) ? now : new Date(cairoTime(ky, km + 1, 1).getTime() - 1);
  return { whole: { from: cairoTime(y, m, 1) }, now: last };
};

// D105: the 12 bars ending with `key`, each month's £ from the day summaries ("YYYY-MM-DD" →
// { revenue }); a month with no summary is £0.
export const revenueBars = (days, key) => {
  const keys = Array.from({ length: 12 }, (_, i) => shift(key, i - 11));
  const totals = Object.fromEntries(keys.map((k) => [k, 0]));
  (days || new Map()).forEach((d, id) => {
    const k = String(id).slice(0, 7);
    if (k in totals) totals[k] += Number(d && d.revenue) || 0;
  });
  return keys.map((k) => ({
    key: k,
    short: MONTH_SHORT[partsOf(k)[1] - 1],
    full: monthLabel(k),
    value: round2(totals[k]),
    chosen: k === key,
  }));
};

// D105: the line under the chart — the chosen month against the month before, the way the
// Gross revenue pill compares them (D78). null while either side is unknown.
export const revenueLine = (gross, windows) => {
  if (!gross || gross.cur == null || gross.prev == null) return null;
  const name = MONTH_NAME[partsOf(windows.key)[1] - 1];
  const before = MONTH_NAME[partsOf(shift(windows.key, -1))[1] - 1];
  const pct = changePct(gross.cur, gross.prev);
  if (pct == null) return { tone: "flat", text: `${before} had no revenue to compare with.` };
  const size = Math.round(Math.abs(pct));
  const tone = size === 0 ? "flat" : pct > 0 ? "up" : "down";
  const way = pct > 0 ? "above" : "below";
  if (windows.current) {
    return {
      tone,
      text:
        tone === "flat"
          ? `${name} so far matches the same days of ${before}.`
          : `${name} so far is ${size}% ${way} the same days of ${before}.`,
    };
  }
  return { tone, text: tone === "flat" ? `${name} matched ${before}.` : `${name} was ${size}% ${way} ${before}.` };
};

// D106: Sessions in and out — five rows, each bar against the largest. `tone` names the fill.
export const MOVE_ROWS = [
  { key: "sold", label: "Sold", tone: "brand" },
  { key: "used", label: "Used by lessons", tone: "info" },
  { key: "returned", label: "Returned by cancels", tone: "warning" },
  { key: "given", label: "Given by staff", tone: "neutral" },
  { key: "referral", label: "Referral rewards", tone: "success" },
];

export const moveRows = (moves) => {
  const values = MOVE_ROWS.map((r) => (moves ? moves[r.key] : null));
  const max = Math.max(0, ...values.map((v) => Number(v) || 0));
  return MOVE_ROWS.map((r, i) => {
    const v = values[i];
    return {
      ...r,
      value: v == null ? "—" : sessionsLabel(v),
      fill: v == null || max === 0 ? 0 : Math.max(0, v) / max,
      note: r.key === "given" && moves && moves.took > 0 ? `${money(moves.gave)} given · ${money(moves.took)} taken back` : null,
    };
  });
};

// D108: the five real tutors owed the most today, with the running cycle's paid hours
// (cycleTeaching's `hours`); the most owed first, then by name.
export const payoutRows = (tutors, teaching, limit = 5) =>
  (tutors || [])
    .filter((t) => !t.isInternal && Number(t.unpaidEarning) > 0)
    .sort(
      (a, b) =>
        Number(b.unpaidEarning) - Number(a.unpaidEarning) || String(a.name || "").localeCompare(String(b.name || ""))
    )
    .slice(0, limit)
    .map((t) => ({
      id: t.id,
      name: displayName(t, "Unknown tutor"),
      hours: `${hoursText((teaching && teaching[t.id] && teaching[t.id].hours) || 0)} this cycle`,
      owed: egp(t.unpaidEarning),
    }));

// D109: the page's numbers for the month, then the chart's 12 months. Numbers go out as
// numbers, an unknown one as an empty cell; "Days" is the Cairo days each row counts. The 12
// months are always written (R61) — `bars` empty (the chart has not landed) falls back to the
// 12 keys with an unknown value, rather than leaving the months out.
export const OVERVIEW_CSV_HEADER = ["Number", "Value", "Days"];
export const overviewCsv = (n, bars, windows, now = new Date()) => {
  const days = rangeLabel(windows.soFar);
  const today = rangeLabel({ from: now, to: now });
  const m = n.moves || {};
  const cell = (x) => (x == null ? "" : x);
  const monthDays = (key) => {
    const [y, mo] = partsOf(key);
    const end = cairoTime(y, mo + 1, 1);
    return rangeLabel({ from: cairoTime(y, mo, 1), to: end > now ? now : end });
  };
  const months = bars && bars.length ? bars : revenueBars(null, windows.key).map((b) => ({ ...b, value: null }));
  const rows = [
    ["Gross revenue (£)", cell(n.gross.cur), days],
    ["Sessions sold", cell(n.sold), days],
    ["£ a session", cell(perSession(n)), days],
    ["Tutor pay owed (E£)", cell(n.owed && n.owed.total), today],
    ["Paid to tutors (E£)", cell(n.paid && n.paid.total), days],
    ["Payments recorded", cell(n.paid && n.paid.count), days],
    ["Sessions used by lessons", cell(m.used), days],
    ["Sessions returned by cancels", cell(m.returned), days],
    ["Sessions given by staff", cell(m.given), days],
    ["Referral reward sessions", cell(m.referral), days],
    ...months.map((b) => [`Revenue ${b.full} (£)`, cell(b.value), monthDays(b.key)]),
  ];
  return { text: csvText(OVERVIEW_CSV_HEADER, rows), filename: `financial-${windows.key}-${cairoDateKey(now)}.csv` };
};
